import { useState, useEffect, useCallback } from 'react'
import type { Board, Card } from '../types'
import { api } from '../api'
import KanbanColumn from '../components/KanbanColumn'
import CardModal from '../components/CardModal'

export default function BoardPage() {
  const [board, setBoard] = useState<Board | null>(null)
  const [cards, setCards] = useState<Card[]>([])
  const [selectedCard, setSelectedCard] = useState<Card | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [newCardColuna, setNewCardColuna] = useState<string>('')
  const [filterCategoria, setFilterCategoria] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    const boards = await api.getBoards()
    if (boards.length === 0) return
    const b = boards[0]
    setBoard(b)
    const c = await api.getCards(b.id)
    setCards(c)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleDrop = async (cardId: number, colunaId: string) => {
    const existing = cards.filter((c) => c.coluna_id === colunaId)
    await api.moveCard(cardId, colunaId, existing.length)
    load()
  }

  const openNew = (colunaId: string) => {
    setSelectedCard(null)
    setNewCardColuna(colunaId)
    setShowModal(true)
  }

  const openEdit = (card: Card) => {
    setSelectedCard(card)
    setNewCardColuna('')
    setShowModal(true)
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full text-gray-400">Carregando...</div>
  }

  if (!board) {
    return <div className="flex items-center justify-center h-full text-gray-400">Nenhum quadro encontrado</div>
  }

  const filteredCards = filterCategoria
    ? cards.filter((c) => c.categoria === filterCategoria)
    : cards

  const categorias = [...new Set(cards.map((c) => c.categoria))]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{board.nome}</h1>
          <p className="text-sm text-gray-500">{cards.length} cards ativos</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="text-sm border rounded-lg px-3 py-1.5"
          >
            <option value="">Todas categorias</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => openNew('ideia')}
            className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            + Novo Card
          </button>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto p-4">
        <div className="flex gap-4 h-full">
          {board.colunas.map((col) => (
            <KanbanColumn
              key={col.id}
              coluna={col}
              cards={filteredCards.filter((c) => c.coluna_id === col.id).sort((a, b) => a.ordem - b.ordem)}
              onCardClick={openEdit}
              onDrop={handleDrop}
              onNewCard={() => openNew(col.id)}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && board && (
        <CardModal
          card={selectedCard}
          colunas={board.colunas}
          boardId={board.id}
          defaultColuna={newCardColuna}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load() }}
        />
      )}
    </div>
  )
}
